import type { BlockType } from '@lighthouse/core'

import type { DesignSettingSwitch } from '../DesignSetting'
import type { InteractionSettingSwitch, StyleSettingSwitch } from '../types'

type BlockLimitOption = {
    design: DesignSettingSwitch
}

export const getBlockAndPageDesignLimit = (
    type: BlockType | 'page' | 'customView' | 'relative' | 'fixed',
    BlockLimitOption?: BlockLimitOption
): StyleSettingSwitch => {
    switch (type) {
        case 'page': {
            return {
                size: {
                    disableWidth: true,
                    // disableHeight: true,
                    hideHeight: true,
                    // hideMinMaxRectSizeAdder: true,
                    hideMaxHeight: true,
                    hideMinHeight: true,
                    hideMinWidth: true,
                    hideOverflow: true
                },
                layout: {
                    disableDistributedAlignment: true
                },
                design: {
                    enableBackground: true,
                    enableVeins: true
                }
            }
        }
        case 'breadcrumb': {
            return {
                size: {
                    disableWidthAuto: true,
                    hideOverflow: true
                }
            }
        }
        case 'image':
        case 'video':
        case 'qrBarcode': {
            return {
                size: {
                    disableWidthAuto: true,
                    hideHeight: true,
                    hideMaxHeight: true,
                    hideMinHeight: true
                },
                design: {
                    enableRadius: true,
                    enableColor: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                }
            }
        }
        case 'card':
        case 'collapse':
        case 'fieldGroup':
        case 'subForm':
        case 'view': {
            return {
                size: {
                    disableWidthAuto: true
                },
                design: {
                    enableRadius: true,
                    enableColor: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                }
            }
        }
        case 'filter': {
            return {
                size: {
                    disableWidthAuto: true
                },
                font: {
                    enableFontFamily: true
                },
                design: {
                    enableTheme: true,
                    enableRadius: true,
                    enableLabelColor: BlockLimitOption?.design?.enableLabelColor ?? true,
                    enableColor: true,
                    enableIconColor: true,
                    enableBackground: true,
                    enableBorder: true,
                    enableShadow: true
                }
            }
        }
        case 'field': {
            return {
                size: {
                    disableWidthAuto: true
                },
                font: {
                    enableFontFamily: true
                }
            }
        }
        case 'tabs': {
            return {
                size: {
                    disableWidthAuto: true,
                    hideOverflow: true
                },
                design: {
                    enableRadius: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableShadow: true
                },
                font: {
                    collapsible: true,
                    enableAdvance: true,
                    enableAlign: true,
                    enableFontStyle: true,
                    enableFontWeight: true,
                    enableFontFamily: true,
                    enableFontSize: true,
                    enableLineHeight: true,
                    enableLetterSpacing: true,
                    enableLineClamp: true
                }
            }
        }
        case 'button': {
            return {
                size: {
                    hideHeight: true,
                    hideMaxHeight: true,
                    hideMinHeight: true,
                    hideOverflow: true
                },
                design: {
                    enableRadius: true,
                    enableColor: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                }
            }
        }
        case 'chart': {
            return {
                size: {
                    disableWidthAuto: true,
                    hideHeight: true,
                    hideMaxHeight: true,
                    hideMinHeight: true,
                    hideOverflow: true
                },
                design: {
                    enableRadius: true,
                    enableColor: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                }
            }
        }
        case 'container':
        case 'formContainer': {
            return {
                size: {},
                layout: {},
                design: {
                    enableRadius: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                }
            }
        }

        case 'customView': {
            return {
                // size: {
                //     disableWidthAuto: true
                // },
                layout: {
                    disableDistributedAlignment: true
                },
                design: {
                    enableRadius: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true
                }
            }
        }

        case 'audio': {
            return {
                size: {
                    disableWidthAuto: true,
                    disableHeightAuto: true,
                    hideOverflow: true
                }
            }
        }
        case 'divider': {
            return {
                size: {
                    disableWidthAuto: true,
                    disableHeightAuto: true,
                    hideOverflow: true
                },
                design: {
                    enableRadius: true,
                    enableColor: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                }
            }
        }
        case 'iframe':
        case 'file': {
            return {
                size: {
                    disableWidthAuto: true,
                    disableHeightAuto: true
                },
                design: {
                    enableRadius: true,
                    enableColor: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                }
            }
        }
        case 'text': {
            return {
                size: {},
                design: {
                    enableColor: true,
                    enableShadow: true,
                    enableTextBorder: true
                },
                font: {
                    collapsible: true,
                    enableAdvance: true,
                    enableAlign: true,
                    enableFontStyle: true,
                    enableFontWeight: true,
                    enableFontFamily: true,
                    enableFontSize: true,
                    enableLineHeight: true,
                    enableLetterSpacing: true,
                    enableLineClamp: true
                }
            }
        }
        case 'userCenter': {
            return {
                size: {
                    disableWidthAuto: true,
                    hideHeight: true,
                    hideOverflow: true,
                    hideMaxHeight: true,
                    hideMinHeight: true
                },
                design: {
                    enableRadius: true,
                    enableColor: true,
                    enableBackground: true,
                    enableBorder: true,
                    enableShadow: true
                }
            }
        }
        case 'onOff':
        case 'icon': {
            return {
                size: {
                    disableWidthAuto: true,
                    hideHeight: true,
                    hideOverflow: true,
                    hideMaxHeight: true,
                    hideMinHeight: true
                },
                design: {
                    enableRadius: true,
                    enableColor: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                }
            }
        }

        case 'toc': {
            return {
                size: {
                    disableWidthAuto: true
                },
                design: {
                    enableRadius: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                }
            }
        }
        case 'floatBox':
        case 'relative': {
            return {
                design: {
                    enableRadius: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                }
            }
        }
        case 'fixed': {
            return {
                design: {
                    enableRadius: true,
                    enableBackground: true,
                    enableVeins: true,
                    enableBorder: true,
                    enableShadow: true,
                    enableBlur: true
                },
                size: {
                    disableWidth: true,
                    hideHeight: true
                }
            }
        }
        default: {
            return {}
        }
    }
}

export const getBlockInteractionDesignLimit = (type: BlockType): Partial<InteractionSettingSwitch> | undefined => {
    if (type === 'filter') {
        return {
            hoverStyle: {
                enableColor: true,
                enableIconColor: true,
                enableRadius: true,
                enableBackground: true,
                enableBorder: true,
                enableShadow: true
            },
            activeStyle: {
                enableColor: true,
                enableIconColor: true,
                enableRadius: true,
                enableBackground: true,
                enableBorder: true,
                enableShadow: true
            },
            disabledStyle: {
                enableColor: true,
                enableIconColor: true,
                enableRadius: true,
                enableBackground: true,
                enableBorder: true,
                enableShadow: true
            }
        }
    }
}
